// Shared constants + geometry helpers for the Asbestos FloorCanvas and its sub-components.

export const STATUS_FILL = { red: "rgba(224,59,47,0.30)", green: "rgba(46,139,61,0.28)", unknown: "rgba(245,194,13,0.30)" };
export const STATUS_STROKE = { red: "#E03B2F", green: "#2E8B3D", unknown: "#F4C20D" };
export const STATUS_LABEL = { red: "Asbestos present", green: "No asbestos", unknown: "Pending confirmation" };
export const STATUS_GLYPH = { red: "!", green: "✓", unknown: "?" };  // non-colour cue (colour-blind / B&W safe)
export const VERTEX_SNAP_PX = 10;   // image-space radius for snapping to a zone vertex
export const UNIT_TO_M = { m: 1, ft: 0.3048, cm: 0.01, mm: 0.001 };

// Asbestos record fields (Register expansion)
export const ASBESTOS_TYPES = ["Chrysotile (white)", "Amosite (brown)", "Crocidolite (blue)", "Mixed", "Not sampled", "Unknown"];
export const ASBESTOS_CONDITIONS = ["Good", "Fair", "Poor", "Damaged"];
export const ASBESTOS_RISK_RATINGS = ["High", "Medium", "Low"];
export const ASBESTOS_ACTIONS = ["Monitor", "Encapsulate", "Remove", "Label", "No action required"];
export const RISK_COLOR = { High: "#E03B2F", Medium: "#F4A623", Low: "#2E8B3D" };
export const DOC_TYPE_OPTIONS = ["Asbestos survey", "Laboratory results", "Clearance certificate", "Removal certificate", "Other"];

export function zoneCentroid(z) {
    if (z.shape === "rectangle") { const [x1, y1, x2, y2] = z.coords; return { x: (x1 + x2) / 2, y: (y1 + y2) / 2 }; }
    if (z.shape === "circle") { const [cx, cy] = z.coords; return { x: cx, y: cy }; }
    if (z.shape === "polygon") {
        let sx = 0, sy = 0, n = 0;
        for (let i = 0; i < z.coords.length; i += 2) { sx += z.coords[i]; sy += z.coords[i + 1]; n++; }
        return n ? { x: sx / n, y: sy / n } : { x: 0, y: 0 };
    }
    return { x: 0, y: 0 };
}

export function zoneAreaPx(z) {
    if (z.shape === "rectangle") { const [x1, y1, x2, y2] = z.coords; return Math.abs((x2 - x1) * (y2 - y1)); }
    if (z.shape === "circle") { const r = z.coords[2]; return Math.PI * r * r; }
    if (z.shape === "polygon") {
        const c = z.coords; const n = c.length / 2; let a = 0;
        for (let i = 0; i < n; i++) { const x1 = c[2 * i], y1 = c[2 * i + 1], x2 = c[2 * ((i + 1) % n)], y2 = c[2 * ((i + 1) % n) + 1]; a += x1 * y2 - x2 * y1; }
        return Math.abs(a / 2);
    }
    return 0;
}

export function fmtLen(px, ppm, unit) {
    if (!ppm) return null;
    const m = px / ppm;
    return unit === "ft" ? `${(m * 3.28084).toFixed(2)} ft` : `${m.toFixed(2)} m`;
}

export function fmtArea(px2, ppm, unit) {
    if (!ppm) return null;
    const m2 = px2 / (ppm * ppm);
    return unit === "ft" ? `${(m2 * 10.7639).toFixed(1)} ft²` : `${m2.toFixed(2)} m²`;
}

// Schematic room dimension label, unit-aware (metric or imperial).
export function fmtRoomDim(r, unit) {
    if (unit === "m") {
        if (r.width_m != null && r.length_m != null) return `${r.width_m} × ${r.length_m} m`;
    } else {
        if (r.width_label && r.length_label) return `${r.width_label} × ${r.length_label}`;
        if (r.width_m != null && r.length_m != null) return `${(r.width_m * 3.28084).toFixed(1)} × ${(r.length_m * 3.28084).toFixed(1)} ft`;
    }
    if (r.width_label && r.length_label) return `${r.width_label} × ${r.length_label}`;
    if (r.width_m != null && r.length_m != null) return `${r.width_m} × ${r.length_m} m`;
    return null;
}

export function r0(n) { return Math.round(n); }
